const DESKTOP_VISIBLE_COUNT = 6;

const SCROLL_LOCK_COUNT_KEY = 'mediaPlayerScrollLockCount';
const SCROLL_LOCK_Y_KEY = 'mediaPlayerScrollY';
const SCROLL_LOCK_PREV_POSITION_KEY = 'mediaPlayerPrevPosition';
const SCROLL_LOCK_PREV_TOP_KEY = 'mediaPlayerPrevTop';
const SCROLL_LOCK_PREV_LEFT_KEY = 'mediaPlayerPrevLeft';
const SCROLL_LOCK_PREV_RIGHT_KEY = 'mediaPlayerPrevRight';
const SCROLL_LOCK_PREV_WIDTH_KEY = 'mediaPlayerPrevWidth';

const lockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount === 0) {
    const scrollY = window.scrollY || window.pageYOffset || 0;

    body.dataset[SCROLL_LOCK_Y_KEY] = String(scrollY);
    body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] = body.style.position || '';
    body.dataset[SCROLL_LOCK_PREV_TOP_KEY] = body.style.top || '';
    body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] = body.style.left || '';
    body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] = body.style.right || '';
    body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] = body.style.width || '';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('media-player-open');
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount + 1);
};

const unlockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount <= 1) {
    const scrollY = Number(body.dataset[SCROLL_LOCK_Y_KEY] || '0');

    body.style.position = body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] || '';
    body.style.top = body.dataset[SCROLL_LOCK_PREV_TOP_KEY] || '';
    body.style.left = body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] || '';
    body.style.right = body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] || '';
    body.style.width = body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] || '';
    body.classList.remove('media-player-open');

    delete body.dataset[SCROLL_LOCK_COUNT_KEY];
    delete body.dataset[SCROLL_LOCK_Y_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_POSITION_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_TOP_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_LEFT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY];

    window.scrollTo(0, scrollY);
    return;
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount - 1);
};

const setupImageGalleryLoadMore = (root) => {
  const items = Array.from(root.querySelectorAll('[data-image-gallery-item]'));
  const loadMoreButton = root.querySelector('[data-image-gallery-load-more]');

  if (!items.length || !loadMoreButton) {
    return;
  }

  let expanded = false;

  const syncVisibility = () => {
    if (expanded) {
      items.forEach((item) => item.classList.remove('is-hidden'));
      loadMoreButton.style.display = 'none';
      return;
    }

    items.forEach((item, index) => {
      item.classList.toggle('is-hidden', index >= DESKTOP_VISIBLE_COUNT);
    });

    loadMoreButton.style.display = items.length > DESKTOP_VISIBLE_COUNT ? '' : 'none';
  };

  loadMoreButton.addEventListener('click', () => {
    expanded = true;
    syncVisibility();
  });

  window.addEventListener('resize', syncVisibility);
  syncVisibility();
};

const setupImageGalleryPlayer = (root) => {
  const galleryRoot = root.closest('.image-gallery');
  const galleryPlayers = galleryRoot
    ? Array.from(galleryRoot.querySelectorAll('[data-image-gallery-player]'))
    : [root];
  const trigger = root.querySelector('.media-player-trigger');
  const modal = root.querySelector('.media-player-modal');
  const stage = root.querySelector('[data-image-gallery-stage]');
  const closeButtons = root.querySelectorAll('[data-image-gallery-close]');
  const prevButton = root.querySelector('[data-image-gallery-prev]');
  const nextButton = root.querySelector('[data-image-gallery-next]');
  const imageUrl = root.getAttribute('data-image-url') || '';

  if (!trigger || !modal || !stage || !imageUrl) {
    return;
  }

  let currentIndex = Math.max(0, galleryPlayers.indexOf(root));

  const updateNavButtons = () => {
    if (!prevButton || !nextButton) {
      return;
    }

    prevButton.disabled = currentIndex <= 0;
    nextButton.disabled = currentIndex >= galleryPlayers.length - 1;
  };

  const renderImage = (index) => {
    const targetRoot = galleryPlayers[index];
    if (!targetRoot) {
      return;
    }

    currentIndex = index;
    const targetUrl = targetRoot.getAttribute('data-image-url') || '';
    const targetAlt = targetRoot.getAttribute('data-image-alt') || 'Gallery image';

    stage.innerHTML = '';
    const image = document.createElement('img');
    image.src = targetUrl;
    image.alt = targetAlt;
    stage.appendChild(image);

    updateNavButtons();
  };

  const openModal = () => {
    renderImage(Math.max(0, galleryPlayers.indexOf(root)));

    root.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    lockPageScroll();
  };

  const closeModal = () => {
    const activeElement = document.activeElement;
    if (activeElement && modal.contains(activeElement)) {
      trigger.focus();
    }

    root.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    stage.innerHTML = '';
    unlockPageScroll();
  };

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    openModal();
  });

  prevButton?.addEventListener('click', (event) => {
    event.preventDefault();

    if (currentIndex <= 0) {
      return;
    }

    renderImage(currentIndex - 1);
  });

  nextButton?.addEventListener('click', (event) => {
    event.preventDefault();

    if (currentIndex >= galleryPlayers.length - 1) {
      return;
    }

    renderImage(currentIndex + 1);
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      closeModal();
    });
  });

  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape' && root.classList.contains('is-open')) {
      closeModal();
      return;
    }

    if (event.key === 'ArrowLeft' && currentIndex > 0) {
      renderImage(currentIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight' && currentIndex < galleryPlayers.length - 1) {
      renderImage(currentIndex + 1);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.image-gallery').forEach((root) => {
    setupImageGalleryLoadMore(root);
  });

  document.querySelectorAll('[data-image-gallery-player]').forEach((root) => {
    setupImageGalleryPlayer(root);
  });
});